import type { DiscoverItem, SortMetric } from '@/types/Discover';
import type { DiscoverPeriod } from '@/constants/Discover';
import { StaticColumnName } from '@/constants/Discover';
import type { METRICS_DATABASE_NAME } from '@/utils/metrics';
import type { Ref, ComputedRef, MaybeRef } from 'vue';
import { computed, unref } from 'vue';
import useStrategyMask from './useStrategyMask';
import type { DSBPortfolioItemResponseDTO, DSBStrategyItemResponseDTO } from '@/api-v2/web/discover';
import { AnalyticsTrackTypeConstants } from '@/types/AnalyticsTrackTypeConstants';
import type { StrategyLookupMap } from './useDiscoverStore';
import { useDiscoverStore } from './useDiscoverStore';

export const useGetDataDiscoverSortUtils = ({
  sortMetric,
  sortOrder,
  sortTimePeriod,
  compareMetric,
}: {
  sortMetric: Ref<SortMetric>;
  sortOrder: Ref<number>;
  sortTimePeriod: Ref<DiscoverPeriod>;
  compareMetric: (
    metric: METRICS_DATABASE_NAME,
    timePeriod: DiscoverPeriod,
    order: number,
  ) => (a: DiscoverItem, b: DiscoverItem) => number;
}): {
  sortItems: (items: DiscoverItem[]) => DiscoverItem[];
} => {
  const { getStrategyMask } = useStrategyMask();

  const compareName = (order: number): ((a: DiscoverItem, b: DiscoverItem) => number) => {
    return (a: DiscoverItem, b: DiscoverItem) => getStrategyMask(a).localeCompare(getStrategyMask(b)) * order;
  };

  const compareLiveDate = (order: number): ((a: DiscoverItem, b: DiscoverItem) => number) => {
    return (a: DiscoverItem, b: DiscoverItem) => {
      const dateA = a.inceptionDate;
      const dateB = b.inceptionDate;
      if (dateA === null) return 1;

      if (dateB === null) return -1;

      if (dateB < dateA) return 1 * order;

      if (dateA < dateB) return -1 * order;

      return 0;
    };
  };

  /**
   * Sort by name ascending (case-insensitive, in code point order) by default -- match postgres output
   * This is now needed because we have merged strategies and portfolios into the same list
   */
  const sortItems = (items: DiscoverItem[]): DiscoverItem[] => {
    if (sortOrder.value === 0) {
      return items
        .map((o) => [getStrategyMask(o).toLowerCase(), o] as const)
        .sort(([a], [b]) => (a > b ? 1 : -1))
        .map(([, o]) => o);
    }

    if (sortMetric.value === StaticColumnName.NAME) {
      return items.sort(compareName(sortOrder.value));
    }

    if (sortMetric.value === StaticColumnName.LIVE_DATE) {
      return items.sort(compareLiveDate(sortOrder.value));
    }

    return items.sort(compareMetric(sortMetric.value, sortTimePeriod.value, sortOrder.value));
  };

  return {
    sortItems,
  };
};

export function getDiscoverItemBySlugOrCode(
  slugOrCode: string | null | undefined,
  {
    allStrategiesByCode,
    allStrategiesById,
    allPortfoliosBySlug,
  }: {
    allStrategiesByCode: ReadonlyMap<string, DSBStrategyItemResponseDTO>;
    allStrategiesById: ReadonlyMap<string, DSBStrategyItemResponseDTO>;
    allPortfoliosBySlug: ReadonlyMap<string, DSBPortfolioItemResponseDTO>;
  },
): DSBPortfolioItemResponseDTO | DSBStrategyItemResponseDTO | null | undefined {
  if (slugOrCode === null) return null;

  if (slugOrCode === undefined) return undefined;

  return (
    allStrategiesByCode.get(slugOrCode) ?? allStrategiesById.get(slugOrCode) ?? allPortfoliosBySlug.get(slugOrCode)
  );
}

export function useGetDiscoverIndexAndType(slugOrCodeRef: Ref<string | null | undefined>): {
  /**
   * `null` means that there is no track.
   *
   * `undefined` means that there was a problem fetching the entity associated with the given `slugOrCode`
   */
  index: ComputedRef<DSBPortfolioItemResponseDTO | DSBStrategyItemResponseDTO | null | undefined>;
  type: ComputedRef<AnalyticsTrackTypeConstants | null>;
} {
  return {
    index: useGetDiscoverIndex(slugOrCodeRef),
    type: useGetDiscoverIndexTypeName(slugOrCodeRef),
  };
}
/**
 * Resolve a track/portfolio by code, id, slug, or portfolio id.
 * Includes equities via `allTracksByCode` / `allTracksById`.
 */
export const getDiscoverIndexBySymbol = (
  symbol: string,
  allTracksByCode: StrategyLookupMap,
  allTracksById: StrategyLookupMap,
  allPortfoliosBySlug: Map<string, DSBPortfolioItemResponseDTO>,
  allPortfoliosById: Map<string, DSBPortfolioItemResponseDTO>,
): DSBPortfolioItemResponseDTO | DSBStrategyItemResponseDTO | undefined => {
  return (
    allTracksByCode.get(symbol) ??
    allTracksById.get(symbol) ??
    allPortfoliosBySlug.get(symbol) ??
    allPortfoliosById.get(symbol)
  );
};

/**
 * `null` means that `null` was provided to this function
 *
 * `undefined` means that `undefined` was provided to this function or that there was a problem
 *   fetching the track associated with the given `slugOrCode` or that the track is not a strategy or portfolio
 */
export function useGetDiscoverIndex(
  slugOrCodeRef: Ref<string | null | undefined>,
): ComputedRef<DSBPortfolioItemResponseDTO | DSBStrategyItemResponseDTO | null | undefined> {
  const { allTracksByCode, allTracksById, allPortfoliosBySlug, allPortfoliosById } = useDiscoverStore();

  return computed((): DSBPortfolioItemResponseDTO | DSBStrategyItemResponseDTO | null | undefined => {
    const symbol = unref(slugOrCodeRef);

    if (symbol === null) return null;

    if (symbol === undefined) return undefined;

    return getDiscoverIndexBySymbol(
      symbol,
      allTracksByCode.value,
      allTracksById.value,
      allPortfoliosBySlug.value,
      allPortfoliosById.value,
    );
  });
}

export function useGetDiscoverIndices(
  symbolsRef: Ref<string[] | [] | undefined>,
): ComputedRef<(DSBPortfolioItemResponseDTO | DSBStrategyItemResponseDTO)[] | undefined | []> {
  const { allTracksByCode, allTracksById, allPortfoliosBySlug, allPortfoliosById } = useDiscoverStore();

  return computed((): (DSBPortfolioItemResponseDTO | DSBStrategyItemResponseDTO)[] | undefined | [] => {
    const symbols = unref(symbolsRef);
    if (symbols === undefined) return undefined;

    const results = symbols
      .map((symbol) =>
        getDiscoverIndexBySymbol(
          symbol,
          allTracksByCode.value,
          allTracksById.value,
          allPortfoliosBySlug.value,
          allPortfoliosById.value,
        ),
      )
      .filter((item): item is DSBPortfolioItemResponseDTO | DSBStrategyItemResponseDTO => !!item);

    return results;
  });
}

export function useGetDiscoverIndexTypeName(
  symbolRef: MaybeRef<string | null | undefined>,
): ComputedRef<AnalyticsTrackTypeConstants | null> {
  const { allTracksByCode, allTracksById, allPortfoliosBySlug, allPortfoliosById } = useDiscoverStore();

  return computed(() => {
    const symbol = unref(symbolRef);
    if (!symbol) return null;

    if (allPortfoliosBySlug.value.has(symbol) || allPortfoliosById.value.has(symbol)) {
      return AnalyticsTrackTypeConstants.PORTFOLIO;
    }

    if (allTracksByCode.value.has(symbol) || allTracksById.value.has(symbol)) {
      return AnalyticsTrackTypeConstants.TRACK;
    }

    return null;
  });
}
